import { z } from "zod";
import { CountryCode } from "../common/country.js";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { IsoDate, Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { VerificationStatus } from "./enterprise.routes.js";
const Ops = { surface: "operator", auth: "operator", permission: "ops:verification.review" };
const Reason = z.string().trim().min(3).max(2000);
/**
 * The sanctions lists an operator screens against (ADR 0026): the UN Security Council's consolidated list and MAS's
 * lists, which Singapore enforces, and the US, EU, and UK lists, which Civentia applies as its own policy.
 */
export const SCREENING_LISTS = ["un", "mas", "ofac", "eu", "uk"];
/** A sanctions screening of the enterprise and its directors; a possible match needs a reason (ADR 0026). */
export const Screening = z
    .object({
    lists: z.array(z.enum(SCREENING_LISTS)).min(1).describe("Every list must be checked"),
    result: z.enum(["clear", "possible_match"]),
    reason: z
        .string()
        .trim()
        .max(2000)
        .optional()
        .describe("Why a possible match does not stop the enterprise; at least 20 characters"),
})
    .meta({ id: "Screening" });
/** What an operator checked to approve an enterprise verified by hand (ADR 0026, card 130). */
export const VerificationChecks = z
    .object({
    registry: z.object({
        source: z.string().trim().min(2).max(200).describe("The registry, such as ACRA BizFile"),
        reference: z.string().trim().min(1).max(200).describe("The profile's number, or the page checked"),
        checkedOn: IsoDate,
    }),
    screening: Screening,
    authority: z.object({
        basis: z.enum(["director_in_registry", "company_email_domain", "director_confirmation"]),
        note: z.string().trim().max(500).optional(),
    }),
})
    .meta({ id: "VerificationChecks" });
export const OpsEnterpriseSummary = z
    .object({
    id: Id,
    legalName: z.string(),
    registrationNumber: z.string(),
    country: CountryCode,
    displayName: LocalizedText,
    verificationStatus: VerificationStatus,
    buyerEnabled: z.boolean(),
    sellerEnabled: z.boolean(),
    suspension: z.object({ at: Timestamp, reason: z.string().nullable() }).nullable(),
    screening: z
        .object({ at: Timestamp, result: Screening.shape.result })
        .nullable()
        .describe("The last sanctions screening by an operator (ADR 0026)"),
    closedAt: Timestamp.nullable(),
    createdAt: Timestamp,
})
    .meta({ id: "OpsEnterpriseSummary" });
export const OpsVerificationCase = z
    .object({
    id: Id,
    enterprise: OpsEnterpriseSummary,
    provider: z.enum(["sumsub", "manual"]).describe("Sumsub's hosted flow, or by hand (ADR 0026)"),
    providerApplicantId: z.string().nullable(),
    providerStatus: z.string().nullable(),
    providerResult: z
        .object({
        answer: z.enum(["GREEN", "RED"]).nullable(),
        rejectType: z.enum(["RETRY", "FINAL"]).nullable(),
        rejectLabels: z.array(z.string()),
        moderationComment: z.string().nullable(),
        clientComment: z
            .string()
            .nullable()
            .describe("Sumsub's note for reviewers; never shown to the enterprise"),
        fetchedAt: Timestamp,
    })
        .nullable(),
    reviewStartedAt: Timestamp.nullable().describe("When the enterprise went under review, set once: the start of the 24-hour decision target (card 119)"),
    assigneeOperatorId: Id.nullable(),
    decision: z.enum(["approved", "rejected", "info_requested"]).nullable(),
    decisionReason: z.string().nullable(),
    checks: VerificationChecks.nullable().describe("What the operator checked, for a case approved by hand"),
    decidedAt: Timestamp.nullable(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
})
    .meta({ id: "OpsVerificationCase" });
export const listVerificationCases = defineRoute({
    ...Ops,
    id: "ops.verification.cases.list",
    method: "GET",
    path: "/v1/ops/verification-cases",
    marks: [],
    summary: "The review queue: cases whose enterprise waits for a reviewer, by when their review started, oldest first; or decided cases",
    prd: ["ENT-2", "ADM-2"],
    request: {
        query: z.object({
            state: z.enum(["awaiting_review", "awaiting_provider", "decided"]).default("awaiting_review"),
            assignee: z.enum(["any", "me", "unassigned"]).default("any"),
        }),
    },
    responses: { 200: { description: "Cases", schema: z.object({ items: z.array(OpsVerificationCase) }) } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED"],
});
export const getVerificationCase = defineRoute({
    ...Ops,
    id: "ops.verification.cases.get",
    method: "GET",
    path: "/v1/ops/verification-cases/{caseId}",
    marks: [],
    summary: "One case with Sumsub's full result",
    prd: ["ENT-2", "ADM-2"],
    request: { params: z.object({ caseId: Id }) },
    responses: { 200: { description: "The case", schema: OpsVerificationCase } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const assignVerificationCase = defineRoute({
    ...Ops,
    id: "ops.verification.cases.assign",
    method: "POST",
    path: "/v1/ops/verification-cases/{caseId}/assign",
    marks: [],
    summary: "Take a case, or give it to another reviewer",
    prd: ["ADM-2"],
    request: {
        params: z.object({ caseId: Id }),
        body: z.object({ operatorId: Id.optional().describe("Defaults to the caller") }),
    },
    responses: { 200: { description: "The case", schema: OpsVerificationCase } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const decideVerificationCase = defineRoute({
    ...Ops,
    id: "ops.verification.cases.decide",
    method: "POST",
    path: "/v1/ops/verification-cases/{caseId}/decide",
    marks: [],
    summary: "Approve (the enterprise becomes Verified and may buy), reject, or ask for more information (the enterprise resubmits); a reason is always required. Approving a case verified by hand needs the checks: the registry, a screening against every list, and the applicant's authority; a possible match needs a reason of at least 20 characters (ADR 0026)",
    prd: ["ENT-2", "ENT-3", "ADM-2"],
    request: {
        params: z.object({ caseId: Id }),
        body: z.object({
            decision: z.enum(["approve", "reject", "request_information"]),
            reason: Reason,
            checks: VerificationChecks.optional().describe("Required to approve a case verified by hand"),
        }),
    },
    responses: { 200: { description: "The decided case", schema: OpsVerificationCase } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "VALIDATION_FAILED", "INVALID_TRANSITION"],
});
export const listOpsEnterprises = defineRoute({
    ...Ops,
    id: "ops.enterprises.list",
    method: "GET",
    path: "/v1/ops/enterprises",
    marks: [],
    summary: "Enterprises by name or registration number, newest first",
    prd: ["ENT-3"],
    request: {
        query: z.object({
            q: z.string().trim().min(1).max(100).optional(),
            verificationStatus: VerificationStatus.optional(),
            suspended: z.enum(["true", "false"]).optional(),
            country: CountryCode.optional().describe("Enterprises registered in this country"),
            screeningDue: z
                .enum(["true", "false"])
                .optional()
                .describe("Verified enterprises screened more than 90 days ago, or never, oldest screening first (ADR 0026)"),
        }),
    },
    responses: {
        200: { description: "Enterprises", schema: z.object({ items: z.array(OpsEnterpriseSummary) }) },
    },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED"],
});
export const getOpsEnterprise = defineRoute({
    ...Ops,
    id: "ops.enterprises.get",
    method: "GET",
    path: "/v1/ops/enterprises/{enterpriseId}",
    marks: [],
    summary: "One enterprise with its verification cases and member count",
    prd: ["ENT-3"],
    request: { params: z.object({ enterpriseId: Id }) },
    responses: {
        200: {
            description: "The enterprise",
            schema: z.object({
                enterprise: OpsEnterpriseSummary,
                activeMembers: z.number().int().nonnegative(),
                cases: z.array(OpsVerificationCase.omit({ enterprise: true })),
            }),
        },
    },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const suspendEnterprise = defineRoute({
    ...Ops,
    id: "ops.enterprises.suspend",
    method: "POST",
    path: "/v1/ops/enterprises/{enterpriseId}/suspend",
    marks: ["step-up"],
    summary: "Suspend a verified enterprise: no new trade; paid orders finish",
    prd: ["ENT-3"],
    request: { params: z.object({ enterpriseId: Id }), body: z.object({ reason: Reason }) },
    responses: { 200: { description: "The enterprise", schema: OpsEnterpriseSummary } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "STEP_UP_REQUIRED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const reinstateEnterprise = defineRoute({
    ...Ops,
    id: "ops.enterprises.reinstate",
    method: "POST",
    path: "/v1/ops/enterprises/{enterpriseId}/reinstate",
    marks: ["step-up"],
    summary: "Lift a suspension",
    prd: ["ENT-3"],
    request: { params: z.object({ enterpriseId: Id }), body: z.object({ reason: Reason }) },
    responses: { 200: { description: "The enterprise", schema: OpsEnterpriseSummary } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "STEP_UP_REQUIRED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const recordScreening = defineRoute({
    ...Ops,
    id: "ops.enterprises.screenings.record",
    method: "POST",
    path: "/v1/ops/enterprises/{enterpriseId}/screenings",
    marks: [],
    summary: "Record a sanctions screening of a verified enterprise and its directors, against every list; a possible match needs a reason of at least 20 characters (ADR 0026)",
    prd: ["ENT-3"],
    request: { params: z.object({ enterpriseId: Id }), body: Screening },
    responses: { 200: { description: "The enterprise", schema: OpsEnterpriseSummary } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "VALIDATION_FAILED", "INVALID_TRANSITION"],
});
export const opsVerificationRoutes = [
    listVerificationCases,
    getVerificationCase,
    assignVerificationCase,
    decideVerificationCase,
    listOpsEnterprises,
    getOpsEnterprise,
    suspendEnterprise,
    reinstateEnterprise,
    recordScreening,
];
//# sourceMappingURL=ops-verification.routes.js.map